import { List, ListItem } from "@mui/material";
import { memo, useCallback } from "react";

import { useQueryParams } from "../../../../hooks/useQueryParams";
import DatePickerFilter from "../../CaseList/Filters/FiltersList/components/DatePickerFilter";
import DateRange from "../../CaseList/Filters/FiltersList/components/DateRange";
import MultiSelectSearch from "../../CaseList/Filters/FiltersList/components/MultiSelectSearch";
import RangeSlider from "../../CaseList/Filters/FiltersList/components/RangeSlider";
import SearchOptionGroup from "../../CaseList/Filters/FiltersList/components/SearchOptionGroup";
import SingleSelect from "../../CaseList/Filters/FiltersList/components/SingleSelect";
import SingleValueSliderFilter from "../../CaseList/Filters/FiltersList/components/SingleValueSliderFilter";
import TimePickerFilter from "../../CaseList/Filters/FiltersList/components/TimePickerFilter";

const DrawerFiltersList = ({ filterState, options = { replaceNavigation: true } }) => {
  const { updateFilter, bulkUpdateFilters } = useQueryParams(options);

  const renderFilterUI = useCallback(
    (element) => {
      const currentFilterState = filterState.find((f) => f.fieldName === element.fieldName);
      if (!currentFilterState) {
        return null;
      }

      switch (element.filterType) {
        case "RangeSlider":
          return (
            <RangeSlider
              bulkUpdateFilters={bulkUpdateFilters}
              fieldName={currentFilterState.fieldName}
              isActive={currentFilterState.activeChip.isActive}
              maxValue={currentFilterState.currentValue?.__lte}
              minValue={currentFilterState.currentValue?.__gte}
              title={currentFilterState.title}
            />
          );

        case "MultiSelectSearch":
          return (
            <MultiSelectSearch
              apiUrl={currentFilterState.apiUrl}
              fieldName={currentFilterState.fieldName}
              isActive={currentFilterState.activeChip.isActive}
              nameKey={currentFilterState.nameKey}
              orderingVal={currentFilterState.orderingVal}
              options={currentFilterState.options}
              searchTitle={currentFilterState.searchTitle}
              selectedItems={currentFilterState.currentValue || []}
              title={currentFilterState.title}
              updateFilter={updateFilter}
              valueKey={currentFilterState.valueKey}
            />
          );

        case "SingleSelect":
          return (
            <SingleSelect
              apiUrl={currentFilterState.apiUrl}
              currentValue={currentFilterState.currentValue}
              fieldName={currentFilterState.fieldName}
              isActive={currentFilterState.activeChip.isActive}
              nameKey={currentFilterState.nameKey}
              orderingVal={currentFilterState.orderingVal}
              title={currentFilterState.title}
              updateFilter={updateFilter}
              valueKey={currentFilterState.valueKey}
              defaultValue={currentFilterState?.defaultValue}
              fetchFromAPI={currentFilterState?.fetchFromAPI}
              staticData={currentFilterState?.staticData}
              hideClearBtn={currentFilterState?.hideClearBtn}
            />
          );

        case "SearchOptionGroup":
          return (
            <SearchOptionGroup
              currentValue={currentFilterState.currentValue}
              customFalseLabel={currentFilterState.customFalseLabel}
              customTrueLabel={currentFilterState.customTrueLabel}
              fieldName={currentFilterState.fieldName}
              isActive={currentFilterState.activeChip.isActive}
              title={currentFilterState.title}
              updateFilter={updateFilter}
              hideFalseLabel={currentFilterState?.hideFalseLabel ?? false}
            />
          );

        case "DatePicker":
          return (
            <DatePickerFilter
              currentValue={currentFilterState.currentValue}
              fieldName={currentFilterState.fieldName}
              title={currentFilterState.title}
              updateFilter={updateFilter}
            />
          );

        case "TimePicker":
          return (
            <TimePickerFilter
              currentValue={currentFilterState.currentValue}
              fieldName={currentFilterState.fieldName}
              title={currentFilterState.title}
              updateFilter={updateFilter}
            />
          );

        case "DateRange":
          return (
            <DateRange
              dateFrom={currentFilterState.currentValue?.__gte}
              dateTo={currentFilterState.currentValue?.__lte}
              fieldName={currentFilterState.fieldName}
              isActive={currentFilterState.activeChip.isActive}
              title={currentFilterState.title}
              updateFilter={updateFilter}
            />
          );

        case "SingleValueSliderFilter":
          return (
            <SingleValueSliderFilter
              currentValue={currentFilterState.currentValue}
              fieldName={currentFilterState.fieldName}
              title={currentFilterState.title}
              updateFilter={updateFilter}
              isActive={currentFilterState.activeChip.isActive}
              defaultValue={currentFilterState?.defaultValue}
              minValue={currentFilterState?.minValue}
              maxValue={currentFilterState?.maxValue}
              setToDefaultOnClear={currentFilterState?.setToDefaultOnClear}
            />
          );

        default:
          return null;
      }
    },
    [filterState, updateFilter, bulkUpdateFilters]
  );

  const renderFilterList = useCallback(
    () => (
      <List>
        {filterState.map((filterConfig) => (
          <ListItem key={filterConfig.fieldName}>{renderFilterUI(filterConfig)}</ListItem>
        ))}
      </List>
    ),
    [filterState, renderFilterUI]
  );

  return <div className="h-full overflow-y-auto">{renderFilterList()}</div>;
};

export default memo(DrawerFiltersList);
